import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Trash2,
} from "lucide-react";
import {
  API,
  BOOKING_DAYS,
  BOOKING_TIME_OPTS,
} from "./shared";

export function BookingTab({ authHeader }) {
  const [settings, setSettings] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [blockForm, setBlockForm] = useState({ date: "", time: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [s, b, a] = await Promise.all([
        axios.get(`${API}/admin/booking/settings`, { headers: authHeader() }),
        axios.get(`${API}/admin/blocked-slots`, { headers: authHeader() }),
        axios.get(`${API}/admin/appointments`, { headers: authHeader() }),
      ]);
      setSettings(s.data);
      setBlocked(b.data);
      setBookings(
        a.data
          .filter((x) => x.service)
          .sort((x, y) =>
            `${x.date}${x.time || ""}`.localeCompare(`${y.date}${y.time || ""}`))
          .sort((x, y) => (y.status === "pending_review" ? 1 : 0) - (x.status === "pending_review" ? 1 : 0))
      );
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayWindow = (i) => (settings?.weekly?.[String(i)]?.[0]) || null;

  const setDay = (i, enabled, start, end) => {
    const weekly = { ...settings.weekly };
    if (!enabled) weekly[String(i)] = [];
    else weekly[String(i)] = [[start, end]];
    setSettings({ ...settings, weekly });
  };

  const saveSettings = async () => {
    setSaving(true);
    setMsg("");
    try {
      const res = await axios.put(
        `${API}/admin/booking/settings`,
        { weekly: settings.weekly, slot_minutes: settings.slot_minutes },
        { headers: authHeader() }
      );
      setSettings(res.data);
      setMsg("Availability saved — the booking page now reflects these hours.");
    } catch {
      setMsg("Could not save availability.");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.date) return;
    try {
      await axios.post(`${API}/admin/blocked-slots`, blockForm, { headers: authHeader() });
      setBlockForm({ date: "", time: "", reason: "" });
      load();
    } catch {}
  };

  const unblock = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocked-slots/${id}`, { headers: authHeader() });
      load();
    } catch {}
  };

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/status`, { status }, { headers: authHeader() });
      load();
    } catch {}
  };

  const reviewBooking = async (id, action, amount) => {
    try {
      await axios.post(
        `${API}/admin/bookings/${id}/${action}`,
        action === "accept"
          ? { origin_url: window.location.origin, ...(amount ? { amount: parseFloat(amount) } : {}) }
          : {},
        { headers: authHeader() }
      );
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update the request.");
    }
  };

  const statusLabel = (s) =>
    s === "pending_payment" ? "Pending payment"
    : s === "pending_review" ? "Needs review"
    : s === "awaiting_payment" ? "Awaiting payment"
    : s;

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  const statusCls = (s) =>
    s === "confirmed"
      ? "bg-[#899480]/15 text-[#3F5043]"
      : s === "pending_payment"
        ? "bg-[#AE8F78]/15 text-[#875F45]"
        : s === "pending_review"
          ? "bg-[#D4AF37]/25 text-[#875F45]"
          : s === "awaiting_payment"
            ? "bg-[#D4AF37]/15 text-[#3F5043]"
            : s === "completed"
              ? "bg-[#3F5043]/10 text-[#3F5043]"
              : "bg-red-100 text-red-700";

  if (!settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[#899480]" />
      </div>
    );
  }

  return (
    <div data-testid="booking-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Booking Calendar</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Control when clients can book on the /book page — set weekly hours, block dates or times,
          and manage paid bookings. All payments run through Stripe.
        </p>
      </div>

      {/* Weekly availability */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 sm:p-6 mb-8" data-testid="availability-editor">
        <div className="font-serif text-xl text-[#3F5043] mb-4">Weekly availability (ET)</div>
        <div className="space-y-2 mb-4">
          {BOOKING_DAYS.map((label, i) => {
            const win = dayWindow(i);
            return (
              <div key={label} className="flex flex-wrap items-center gap-3" data-testid={`avail-day-${i}`}>
                <label className="flex items-center gap-2 w-32">
                  <input
                    type="checkbox"
                    data-testid={`avail-day-${i}-toggle`}
                    checked={!!win}
                    onChange={(e) => setDay(i, e.target.checked, "09:00", "17:00")}
                    className="accent-[#3F5043]"
                  />
                  <span className="text-sm text-[#3F5043]">{label}</span>
                </label>
                {win ? (
                  <>
                    <select
                      data-testid={`avail-day-${i}-start`}
                      value={win[0]}
                      onChange={(e) => setDay(i, true, e.target.value, win[1])}
                      className={inputCls}
                    >
                      {BOOKING_TIME_OPTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <span className="text-[#3F5043]/50 text-sm">to</span>
                    <select
                      data-testid={`avail-day-${i}-end`}
                      value={win[1]}
                      onChange={(e) => setDay(i, true, win[0], e.target.value)}
                      className={inputCls}
                    >
                      {BOOKING_TIME_OPTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span className="text-xs text-[#BCB3A6]">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-[#3F5043]">Appointment length</label>
          <select
            data-testid="slot-minutes"
            value={settings.slot_minutes}
            onChange={(e) => setSettings({ ...settings, slot_minutes: Number(e.target.value) })}
            className={inputCls}
          >
            {[30, 45, 60, 90, 120].map((m) => (
              <option key={m} value={m}>{m} min</option>
            ))}
          </select>
          <button
            data-testid="save-availability"
            onClick={saveSettings}
            disabled={saving}
            className="rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save availability"}
          </button>
          {msg && <span className="text-xs text-[#899480]" data-testid="availability-msg">{msg}</span>}
        </div>
      </div>

      {/* Blocked dates/times */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 sm:p-6 mb-8" data-testid="blocked-slots-editor">
        <div className="font-serif text-xl text-[#3F5043] mb-1">Block dates & times</div>
        <p className="text-xs text-[#3F5043]/60 mb-4">
          Block a whole day (leave time empty) or a specific time slot — vacation, holidays, personal time.
        </p>
        <form onSubmit={addBlock} className="flex flex-wrap items-center gap-3 mb-4">
          <input
            data-testid="block-date"
            type="date"
            required
            value={blockForm.date}
            onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
            className={inputCls}
          />
          <select
            data-testid="block-time"
            value={blockForm.time}
            onChange={(e) => setBlockForm({ ...blockForm, time: e.target.value })}
            className={inputCls}
          >
            <option value="">All day</option>
            {BOOKING_TIME_OPTS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input
            data-testid="block-reason"
            placeholder="Reason (optional)"
            value={blockForm.reason}
            onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
            className={`${inputCls} flex-1 min-w-[160px]`}
          />
          <button
            type="submit"
            data-testid="block-submit"
            className="rounded-full px-5 py-2.5 bg-[#875F45] text-[#F8F7F3] text-sm font-medium hover:bg-[#3F5043] transition-colors"
          >
            Block
          </button>
        </form>
        {blocked.length === 0 ? (
          <p className="text-sm text-[#3F5043]/50">No blocked dates or times.</p>
        ) : (
          <div className="space-y-2">
            {blocked.map((b) => (
              <div
                key={b.id}
                data-testid={`blocked-${b.id}`}
                className="flex items-center justify-between gap-3 bg-[#F8F7F3] rounded-2xl px-4 py-2.5"
              >
                <div className="text-sm text-[#3F5043]">
                  <span className="font-medium">{b.date}</span>
                  <span className="mx-2 text-[#BCB3A6]">·</span>
                  {b.time || "All day"}
                  {b.reason && <span className="text-[#3F5043]/60"> — {b.reason}</span>}
                </div>
                <button
                  data-testid={`unblock-${b.id}`}
                  onClick={() => unblock(b.id)}
                  className="text-xs text-[#875F45] hover:text-red-700 inline-flex items-center gap-1"
                >
                  <Trash2 size={13} /> Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Online bookings */}
      <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 sm:p-6" data-testid="online-bookings">
        <div className="font-serif text-xl text-[#3F5043] mb-4">Online bookings</div>
        {bookings.length === 0 ? (
          <p className="text-sm text-[#3F5043]/50">No online bookings yet.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="flex flex-wrap items-center justify-between gap-3 bg-[#F8F7F3] rounded-2xl px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#3F5043] truncate">
                    {b.title}
                    <span className="mx-2 text-[#BCB3A6]">·</span>
                    <span className="font-normal">{b.guest_name || b.client_email}</span>
                  </div>
                  <div className="text-xs text-[#3F5043]/60">
                    {b.date} at {b.time || "—"} ET
                    {b.guest_phone && <span> · {b.guest_phone}</span>}
                    {b.rescheduled_from && <span> · rescheduled from {b.rescheduled_from}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${statusCls(b.status)}`}>
                    {statusLabel(b.status)}
                  </span>
                  {b.status === "pending_review" && (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs text-[#875F45]">
                        $
                        <input
                          type="number"
                          min="1"
                          step="1"
                          defaultValue="80"
                          data-testid={`booking-quote-${b.id}`}
                          onChange={(e) => setQuotes({ ...quotes, [b.id]: e.target.value })}
                          className="w-16 bg-white border border-[#D4AF37]/60 rounded-full px-2 py-1 text-xs text-[#3F5043] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                          title="Custom quote (travel fee etc.) — leave 80 for standard"
                        />
                      </span>
                      <button
                        data-testid={`booking-accept-${b.id}`}
                        onClick={() => reviewBooking(b.id, "accept", quotes[b.id])}
                        className="text-xs rounded-full px-3 py-1.5 bg-[#3F5043] text-[#F8F7F3] font-semibold hover:bg-[#899480] transition-colors"
                      >
                        Accept &amp; send payment link
                      </button>
                      <button
                        data-testid={`booking-decline-${b.id}`}
                        onClick={() => reviewBooking(b.id, "decline")}
                        className="text-xs rounded-full px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                      >
                        Decline
                      </button>
                    </>
                  )}
                  {b.status === "awaiting_payment" && b.checkout_url && (
                    <button
                      data-testid={`booking-copy-pay-${b.id}`}
                      onClick={() => navigator.clipboard.writeText(b.checkout_url).catch(() => {})}
                      className="text-xs rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      Copy payment link
                    </button>
                  )}
                  {(b.is_video || b.service === "video-consult") && b.status === "confirmed" && (
                    <a
                      href={`/video-visit/${b.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`booking-join-video-${b.id}`}
                      className="text-xs rounded-full px-3 py-1.5 bg-[#D4AF37] text-[#3F5043] font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
                    >
                      Join video
                    </a>
                  )}
                  {b.status === "pending_payment" && (
                    <button
                      data-testid={`booking-confirm-${b.id}`}
                      onClick={() => setStatus(b.id, "confirmed")}
                      className="text-xs rounded-full px-3 py-1.5 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#899480] transition-colors"
                    >
                      Confirm
                    </button>
                  )}
                  {b.status === "confirmed" && (
                    <button
                      data-testid={`booking-complete-${b.id}`}
                      onClick={() => setStatus(b.id, "completed")}
                      className="text-xs rounded-full px-3 py-1.5 bg-[#899480] text-white hover:bg-[#3F5043] transition-colors"
                    >
                      Complete
                    </button>
                  )}
                  {["confirmed", "pending_payment"].includes(b.status) && (
                    <button
                      data-testid={`booking-cancel-${b.id}`}
                      onClick={() => setStatus(b.id, "canceled")}
                      className="text-xs rounded-full px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
